import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { MatIconButton, MatButton } from "@angular/material/button";
import { MatCheckbox } from "@angular/material/checkbox";
import { MatFormField, MatLabel, MatSuffix } from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { DecimalPipe } from "@angular/common";
import { ExportDialogData, ExportDialogResult, ExportFormat } from "./export-dialog.models";

@Component({
  selector: "lib-export-dialog",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogModule,
    MatIcon,
    MatIconButton,
    MatButton,
    MatCheckbox,
    MatFormField,
    MatLabel,
    MatSuffix,
    MatInput,
    DecimalPipe,
  ],
  templateUrl: "./export-dialog.component.html",
  styleUrl: "./export-dialog.component.scss",
})
export class ExportDialogComponent {
  readonly data = inject<ExportDialogData>(MAT_DIALOG_DATA);

  readonly dialogRef = inject(MatDialogRef<ExportDialogComponent, ExportDialogResult>);

  /** Show the Max Records section when explicit flag is set, else when there's a text search query. */
  protected readonly showMaxRecords =
    this.data.showMaxRecords !== undefined ? this.data.showMaxRecords : this.data.textSearchQuery != null;

  protected readonly maxRecordsCap = this.data.maxRecordsCap ?? 1000;

  protected readonly selectedFormatId = signal<string>(this.initialFormatId());

  protected readonly records = signal<number>(this.data.maxRecordsDefault ?? 100);

  /** layer-id → checked map */
  protected readonly checked = signal<Record<string, boolean>>(this.initialCheckedMap());

  protected readonly selectedLayerIds = computed<string[] | null>(() => {
    const layers = this.data.layers;
    if (!layers) return null;
    const c = this.checked();
    return layers.filter((l) => c[l.id]).map((l) => l.id);
  });

  protected readonly selectedLayerCount = computed(() => this.selectedLayerIds()?.length ?? 0);

  protected readonly totalSelectedDetections = computed(() => {
    const layers = this.data.layers;
    if (!layers) return 0;
    const c = this.checked();
    return layers.filter((l) => c[l.id]).reduce((sum, l) => sum + (l.count ?? 0), 0);
  });

  protected readonly allChecked = computed(() => {
    const layers = this.data.layers;
    return !!layers && layers.length > 0 && this.selectedLayerCount() === layers.length;
  });

  protected readonly canExport = computed(() => {
    const noLayersOrSomeChecked = !this.data.layers || this.selectedLayerCount() > 0;
    return noLayersOrSomeChecked && !!this.selectedFormatId();
  });

  protected toggleLayer(id: string): void {
    this.checked.update((c) => ({ ...c, [id]: !c[id] }));
  }

  protected setAllLayers(value: boolean): void {
    const layers = this.data.layers;
    if (!layers) return;
    const next: Record<string, boolean> = {};
    layers.forEach((l) => (next[l.id] = value));
    this.checked.set(next);
  }

  protected selectFormat(id: string): void {
    this.selectedFormatId.set(id);
  }

  protected setRecords(raw: string): void {
    const n = raw === "" ? 1 : parseInt(raw, 10) || 1;
    this.records.set(Math.max(1, Math.min(this.maxRecordsCap, n)));
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  protected apply(): void {
    if (!this.canExport()) return;
    const fmt = this.data.formats.find((f) => f.id === this.selectedFormatId()) ?? this.data.formats[0];
    const result: ExportDialogResult = {
      format: fmt,
      layers: this.selectedLayerIds(),
      textSearchQuery: this.data.textSearchQuery,
      ...(this.showMaxRecords ? { records: this.records() } : {}),
    };
    this.dialogRef.close(result);
  }

  protected isSelected(format: ExportFormat): boolean {
    return this.selectedFormatId() === format.id;
  }

  private initialFormatId(): string {
    if (this.data.defaultFormatId) return this.data.defaultFormatId;
    const recommended = this.data.formats.find((f) => f.recommended);
    return recommended?.id ?? this.data.formats[0]?.id ?? "";
  }

  private initialCheckedMap(): Record<string, boolean> {
    const layers = this.data.layers;
    if (!layers) return {};
    const preselect = this.data.preselectedLayerIds;
    const out: Record<string, boolean> = {};
    layers.forEach((l) => {
      out[l.id] = preselect ? preselect.includes(l.id) : true;
    });
    return out;
  }
}
